import type { Component } from "schemas/component";

interface AccessibilityConfig {
  role?: string | undefined;
  hidden?: boolean | undefined;
  checked?: boolean | undefined;
  live?: string | undefined;
  labelledBy?: string | undefined;
  label?: string | undefined;
}

const mapAccessibilityTypeToAttributes: Record<
  keyof AccessibilityConfig,
  string
> = {
  role: "role",
  hidden: "aria-hidden",
  checked: "aria-checked",
  live: "aria-live",
  labelledBy: "aria-labelledby",
  label: "aria-label",
};

export const getAccessibilityAttributes = (
  accessibilityConfig: AccessibilityConfig,
) => {
  const accessibilityAttributes: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(accessibilityConfig)) {
    if (value != null) {
      accessibilityAttributes[
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        mapAccessibilityTypeToAttributes[key as keyof AccessibilityConfig]
      ] = value;
    }
  }

  if (accessibilityConfig.role === "switch") {
    accessibilityAttributes.tabIndex = 0;
  }

  return accessibilityAttributes;
};

// NOTE (Gabe, 2026-08-25): An option list must always expose an accessible name
// (WCAG 4.1.2), so an unlabelled one falls back to the product option name. A
// blank merchant label counts as unlabelled, otherwise clearing the field in the
// editor would emit aria-label="" and reintroduce the violation.
export const getOptionListAccessibilityAttributes = ({
  props,
  optionName,
}: {
  props: Component["props"];
  optionName: string | undefined;
}) => {
  const hidden = props._accessibilityHidden;
  const labelledBy = props._accessibilityLabelledBy;
  return getAccessibilityAttributes({
    hidden,
    labelledBy,
    // NOTE (Gabe, 2026-08-25): aria-hidden and aria-labelledby each make an
    // aria-label redundant, so don't emit one next to them.
    label:
      hidden || labelledBy
        ? undefined
        : props._accessibilityLabel?.trim() || optionName?.trim() || undefined,
  });
};
